import React, { useEffect, useMemo, useState } from "react";
import "./FirmReportMenu.scss";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
} from "../../components/Common/AppOverlay";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Toolbar,
  ButtonComponent,
} from "../../components/Common/LegacyGridAdapter";

const reportGroups = [
  {
    id: "inspection",
    title: "Inspection Reports",
    description: "Reports related to inspection cycles and document access",
    icon: "bx-folder",
    tone: "blue",
    reports: [
      [
        "Inspection Cycle Report",
        "Report on who has accessed the documents.",
        "bx-clipboard",
        "Cycle",
      ],
      [
        "Inspection Summary Report",
        "Report to view the complete inspection details.",
        "bx-line-chart",
        "Summary",
      ],
      [
        "Document Access Report",
        "Document access report.",
        "bx-folder",
        "Access",
      ],
      [
        "Risk Assessment Report",
        "Summary of risks identified across inspections.",
        "bx-shield-quarter",
        "Risk",
      ],
      [
        "Inspection Timeliness Report",
        "Report on inspection cycle timelines and due dates.",
        "bx-time-five",
        "Timeline",
      ],
    ],
  },
  {
    id: "engagement",
    title: "Engagement Reports",
    description: "Reports related to engagements and task status",
    icon: "bx-group",
    tone: "green",
    reports: [
      [
        "Engagement Report",
        "Report on engagements based on status.",
        "bx-file-find",
        "Engagement",
      ],
      [
        "Search Task Report",
        "Report on task status.",
        "bx-search-alt",
        "Tasks",
      ],
      [
        "Engagement Team Report",
        "Report on engagement team assignments and roles.",
        "bx-group",
        "Team",
      ],
      [
        "Upcoming Due Dates Report",
        "Report on upcoming tasks and engagement due dates.",
        "bx-calendar-event",
        "Schedule",
      ],
      [
        "Engagement Progress Report",
        "Overall progress of engagements across stages.",
        "bx-check-circle",
        "Progress",
      ],
      [
        "Overdue Tasks Report",
        "Report on overdue tasks and delayed activities.",
        "bx-check-circle",
        "Overdue",
      ],
    ],
  },
  {
    id: "access",
    title: "Access & Security Reports",
    description: "Reports related to files and access permissions",
    icon: "bx-shield-quarter",
    tone: "orange",
    reports: [
      [
        "Files Detail Report",
        "Report to view the complete file details.",
        "bx-file",
        "Files",
      ],
      [
        "Access & Permissions Report",
        "Report on user access and permissions.",
        "bx-shield",
        "Security",
      ],
      [
        "User Activity Report",
        "Report on user activities and actions performed.",
        "bx-user",
        "Activity",
      ],
      [
        "Login Audit Report",
        "Report on user logins and security events.",
        "bx-lock-alt",
        "Audit",
      ],
    ],
  },
  {
    id: "quality",
    title: "Quality & Findings Reports",
    description: "Reports related to findings, queries and conclusions",
    icon: "bx-flag",
    tone: "rose",
    reports: [
      [
        "Findings Report",
        "Summary of findings raised across engagements.",
        "bx-flag",
        "Findings",
      ],
      [
        "Queries Report",
        "Report on all queries raised during reviews.",
        "bx-question-mark",
        "Queries",
      ],
      [
        "Conclusion Report",
        "Report on final conclusions of engagement reviews.",
        "bx-task",
        "Conclusion",
      ],
      [
        "Review Outcome Report",
        "Summary of review outcomes (passed, conditional, or failed).",
        "bx-bookmark",
        "Outcome",
      ],
      [
        "Partner Review Report",
        "Report on partner review and sign-off status.",
        "bx-bar-chart-alt-2",
        "Review",
      ],
    ],
  },
  {
    id: "custom",
    title: "Custom Reports",
    description: "Saved and custom reports",
    icon: "bx-customize",
    tone: "purple",
    reports: [
      [
        "Data Export Report",
        "Report on data exports and downloads.",
        "bx-download",
        "Export",
      ],
      [
        "Compliance Dashboard Report",
        "Summary of compliance activity and status.",
        "bx-chart",
        "Compliance",
      ],
      [
        "Review Portfolio Report",
        "Portfolio view of review activity across the firm.",
        "bx-briefcase",
        "Portfolio",
      ],
      [
        "Custom Report Builder",
        "Create a saved report using your preferred fields.",
        "bx-slider-alt",
        "Custom",
      ],
    ],
  },
];

const reportRoutes = {
  "Inspection Cycle Report": "/piers/reports/inspection-cycle",
  "Inspection Summary Report": "/piers/reports/inspection-summary",
  "Document Access Report": "/piers/reports/document-access",
  "Risk Assessment Report": "/piers/reports/risk-assessment",
  "Inspection Timeliness Report": "/piers/reports/inspection-timeliness",
  "Engagement Report": "/piers/reports/engagement",
  "Search Task Report": "/piers/reports/search-task",
  "Engagement Team Report": "/piers/reports/engagement-team",
  "Upcoming Due Dates Report": "/piers/reports/upcoming-due-dates",
  "Engagement Progress Report": "/piers/reports/engagement-progress",
  "Overdue Tasks Report": "/piers/reports/overdue-tasks",
  "Files Detail Report": "/piers/reports/files-detail",
  "Access & Permissions Report": "/piers/reports/access-permissions",
  "User Activity Report": "/piers/reports/user-activity",
  "Login Audit Report": "/piers/reports/login-audit",
  "Findings Report": "/piers/reports/findings",
  "Queries Report": "/piers/reports/queries",
  "Conclusion Report": "/piers/reports/conclusion",
  "Review Outcome Report": "/piers/reports/review-outcome",
  "Partner Review Report": "/piers/reports/partner-review",
  "Data Export Report": "/piers/reports/data-export",
  "Compliance Dashboard Report": "/piers/reports/compliance-dashboard",
  "Review Portfolio Report": "/piers/reports/review-portfolio",
  "Custom Report Builder": "/piers/reports/custom-report-builder",
};

const matchesReport = (report, search) =>
  !search ||
  `${report[0]} ${report[1]} ${report[3]}`
    .toLowerCase()
    .includes(search.toLowerCase());

const FirmReportMenu = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [sortBy, setSortBy] = useState("name-asc");
  const [favorites, setFavorites] = useState(new Set());

  const totalReports = useMemo(
    () =>
      reportGroups.reduce((total, group) => total + group.reports.length, 0),
    []
  );
  const visibleGroups = useMemo(
    () =>
      reportGroups
        .filter(
          (group) => selectedGroup === "all" || group.id === selectedGroup
        )
        .map((group) => ({
          ...group,
          reports: group.reports
            .filter((report) => matchesReport(report, search))
            .slice()
            .sort((left, right) =>
              sortBy === "name-desc"
                ? right[0].localeCompare(left[0])
                : left[0].localeCompare(right[0])
            ),
        }))
        .filter((group) => group.reports.length > 0),
    [search, selectedGroup, sortBy]
  );

  const selectGroup = (groupId) => setSelectedGroup(groupId);

  const toggleFavorite = (name) =>
    setFavorites((current) => {
      const next = new Set(current);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  return (
    <main className="report-menu page-content piers-page">
      <Container fluid className="report-menu__container">
        <header className="report-menu__header piers-page-header">
          <div>
            <h1 className="piers-page-title">Reports</h1>
            <p>
              Access and generate reports to gain insights and track performance
              across inspections and engagements.
            </p>
          </div>
          <div className="report-menu__tools">
            <label className="report-menu__search">
              <i className="bx bx-search" aria-hidden="true" />
              <span className="visually-hidden">Search reports</span>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search reports..."
              />
            </label>
          </div>
        </header>

        <div className="report-menu__layout">
          <aside className="report-menu__sidebar">
            <section className="report-menu__side-panel">
              <h2>Report Categories</h2>
              <nav aria-label="Report categories">
                <button
                  type="button"
                  className={`report-menu__category report-menu__category--blue ${
                    selectedGroup === "all" ? "is-selected" : ""
                  }`}
                  onClick={() => selectGroup("all")}
                >
                  <i className="bx bx-group" aria-hidden="true" />
                  <span>
                    <b>All Reports</b>
                    <small>All available reports</small>
                  </span>
                  <em>{totalReports}</em>
                </button>
                {reportGroups.map((group) => (
                  <button
                    type="button"
                    key={group.id}
                    className={`report-menu__category report-menu__category--${
                      group.tone
                    } ${selectedGroup === group.id ? "is-selected" : ""}`}
                    onClick={() => selectGroup(group.id)}
                  >
                    <i className={`bx ${group.icon}`} aria-hidden="true" />
                    <span>
                      <b>{group.title}</b>
                      <small>{group.description}</small>
                    </span>
                    <em>{group.reports.length}</em>
                  </button>
                ))}
              </nav>
            </section>
            <section className="report-menu__side-panel report-menu__quick-actions">
              <h2>Quick Actions</h2>
              <button type="button">
                <i className="bx bx-calendar" aria-hidden="true" />
                <span>
                  <b>Scheduled Reports</b>
                  <small>Manage scheduled reports</small>
                </span>
              </button>
              <button type="button">
                <i className="bx bx-star" aria-hidden="true" />
                <span>
                  <b>Favorite Reports</b>
                  <small>
                    {favorites.size
                      ? `${favorites.size} saved report${
                          favorites.size > 1 ? "s" : ""
                        }`
                      : "View your favorite reports"}
                  </small>
                </span>
              </button>
              <button type="button">
                <i className="bx bx-time-five" aria-hidden="true" />
                <span>
                  <b>Recent Reports</b>
                  <small>View recently generated reports</small>
                </span>
              </button>
            </section>
          </aside>

          <section className="report-menu__content" aria-label="Reports">
            <header className="report-menu__content-header">
              <div>
                <h2>
                  {selectedGroup === "all"
                    ? "All Reports"
                    : reportGroups.find((group) => group.id === selectedGroup)
                        ?.title}
                </h2>
                <span>
                  {selectedGroup === "all"
                    ? totalReports
                    : visibleGroups[0]?.reports.length || 0}{" "}
                  reports available
                </span>
              </div>
              <div className="report-menu__content-tools">
                <label>
                  Sort by:
                  <select
                    value={sortBy}
                    onChange={(event) => setSortBy(event.target.value)}
                  >
                    <option value="name-asc">Report Name (A–Z)</option>
                    <option value="name-desc">Report Name (Z–A)</option>
                  </select>
                </label>
              </div>
            </header>

            <div className="report-menu__groups">
              {visibleGroups.map((group, index) => (
                <section
                  className={`report-menu__group report-menu__group--${group.tone}`}
                  key={group.id}
                  aria-labelledby={`report-group-${group.id}`}
                >
                  <header className="report-menu__group-header">
                    <span className="report-menu__group-icon">
                      <i className={`bx ${group.icon}`} />
                    </span>
                    <span>
                      <b id={`report-group-${group.id}`}>
                        {index + 1}. {group.title}
                      </b>
                      <small>{group.description}</small>
                    </span>
                    <em>{group.reports.length} reports</em>
                  </header>
                  <div className="report-menu__report-list">
                    {group.reports.map((report) => (
                      <article className="report-menu__report" key={report[0]}>
                        <span className="report-menu__report-icon">
                          <i className={`bx ${report[2]}`} />
                        </span>
                        <div className="report-menu__report-copy">
                          <h3>{report[0]}</h3>
                          <p>{report[1]}</p>
                        </div>
                        <button
                          className={`report-menu__favorite ${
                            favorites.has(report[0]) ? "is-favorite" : ""
                          }`}
                          type="button"
                          aria-label={`Favorite ${report[0]}`}
                          onClick={() => toggleFavorite(report[0])}
                        >
                          <i className="bx bxs-star" />
                        </button>
                        <button
                          className="report-menu__view"
                          type="button"
                          onClick={() => navigate(reportRoutes[report[0]])}
                        >
                          View Report
                        </button>
                        <button
                          className="report-menu__more"
                          type="button"
                          aria-label={`More actions for ${report[0]}`}
                        >
                          <i className="bx bx-dots-vertical-rounded" />
                        </button>
                      </article>
                    ))}
                  </div>
                </section>
              ))}
            </div>
            {!visibleGroups.length && (
              <div className="report-menu__empty">
                No reports match your search.
              </div>
            )}
          </section>
        </div>
      </Container>
    </main>
  );
};

export default FirmReportMenu;
